<template>
  <div class="p-4 bg-white rounded-lg poppins h-100" v-if="isIndexDataLoaded">
    <div class="custom-content-height h-100 justify-content-start">
      <!-- Header Section -->
      <div
        class="mb-7 mx-4 mt-6 row flex-grow-0 align-center justify-content-between pe-3"
      >
        <datatable-detail
          class="col-12 col-md-6 col-sm-9"
          :table_properties="indexData?.properties"
        />
        <div v-if="!isClient" class="col-12 col-md-3">
          <v-autocomplete
            :label="$t('client')"
            v-model="customer_id"
            outlined
            dense
            :items="indexData.data.rule_data.clients"
            item-text="text"
            item-value="index"
          />
        </div>
      </div>

      <!-- Rules Panels -->
      <div class="mx-7">
        <v-expansion-panels flat>
          <!-- SKU Restriction Panel -->
          <v-expansion-panel class="mt-0 bg-secondary-o-50 mb-3 rounded-lg">
            <v-expansion-panel-header>
              <div class="d-flex flex-column align-items-start">
                <span class="font-weight-bolder h6 mb-0 poppins py-1"
                  >SKU Restriction</span
                >
                <span class="font-weight-normal text-muted font-size-sm pb-1">
                  Specify which SKUs should be placed on hold or cancelled.
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-0">
                <v-tabs
                  background-color="transparent"
                  v-model="formData.sku_restriction.action"
                >
                  <v-tab>Hold</v-tab>
                  <v-tab>Cancel</v-tab>
                </v-tabs>
                <div class="pt-4 custom-rule-select">
                  <v-autocomplete
                    v-model="formData.sku_restriction[currentSkuAction].value"
                    :label="`SKUs to ${currentSkuActionLabel}`"
                    :items="serverData.sku_options"
                    item-text="text"
                    return-object
                    cache-items
                    outlined
                    clearable
                    multiple
                    small-chips
                    deletable-chips
                    hide-selected
                    :placeholder="$t('enter_at_least_2_characters')"
                    :loading="sku_isLoading"
                    :search-input.sync="sku_search"
                  />
                </div>
                <div class="">
                  <v-textarea
                    v-model="formData.sku_restriction.description"
                    auto-grow
                    label="Description"
                    rows="4"
                    row-height="30"
                    outlined
                  ></v-textarea>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Country Restriction Panel -->
          <v-expansion-panel class="mt-0 bg-secondary-o-50 mb-3 rounded-lg">
            <v-expansion-panel-header>
              <div class="d-flex flex-column align-items-start">
                <span class="font-weight-bolder h6 mb-0 poppins py-1"
                  >Country Restriction</span
                >
                <span class="font-weight-normal text-muted font-size-sm pb-1">
                  Specify which countries should have their orders placed on
                  hold or cancelled.
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-0">
                <v-tabs
                  background-color="transparent"
                  v-model="formData.country_restriction.action"
                >
                  <v-tab>Hold</v-tab>
                  <v-tab>Cancel</v-tab>
                </v-tabs>
                <div class="pt-4 custom-rule-select">
                  <v-autocomplete
                    v-model="
                      formData.country_restriction[currentCountryAction].value
                    "
                    :label="`Countries to ${currentCountryActionLabel}`"
                    :items="
                      getAvailableCountries(
                        'country_restriction',
                        currentCountryAction
                      )
                    "
                    item-text="text"
                    return-object
                    outlined
                    clearable
                    multiple
                    small-chips
                    deletable-chips
                  >
                    <template v-slot:prepend-item>
                      <v-list-item
                        ripple
                        @mousedown.prevent
                        @click="
                          toggleSelectAll(
                            'country_restriction',
                            currentCountryAction
                          )
                        "
                      >
                        <v-list-item-action>
                          <v-icon
                            :color="
                              formData.country_restriction[currentCountryAction]
                                .value?.length > 0
                                ? 'purple darken-2'
                                : ''
                            "
                          >
                            {{
                              getSelectAllIcon(
                                "country_restriction",
                                currentCountryAction
                              )
                            }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>Select All</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2" />
                    </template>
                  </v-autocomplete>
                </div>
                <div class="">
                  <v-textarea
                    v-model="formData.country_restriction.description"
                    auto-grow
                    label="Description"
                    rows="4"
                    row-height="30"
                    outlined
                  ></v-textarea>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- AI Phone Correction Panel -->
          <v-expansion-panel
            class="mt-0 bg-secondary-o-50 mb-3 rounded-lg"
            v-if="!isClient"
          >
            <v-expansion-panel-header>
              <div class="d-flex flex-column align-items-start">
                <span class="font-weight-bolder h6 mb-0 poppins py-1"
                  >AI phone correction</span
                >
                <span class="font-weight-normal text-muted font-size-sm pb-1">
                  Automatically detect and correct invalid or incorrectly
                  formatted phone numbers using AI before order processing.
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-0">
                <v-checkbox
                  v-model="formData.ai_phone_correction.enabled"
                  color="purple darken-2"
                  :true-value="1"
                  :false-value="0"
                >
                  <template v-slot:label>
                    <span class="h6 mb-0 text-dark-75 poppins">Enabled</span>
                  </template>
                </v-checkbox>
              </div>
              <div class="">
                <v-textarea
                  v-model="formData.ai_phone_correction.description"
                  auto-grow
                  label="Description"
                  rows="4"
                  row-height="30"
                  outlined
                ></v-textarea>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- AI Address Correction Panel -->
          <v-expansion-panel
            class="mt-0 bg-secondary-o-50 mb-3 rounded-lg"
            v-if="!isClient"
          >
            <v-expansion-panel-header>
              <div class="d-flex flex-column align-items-start">
                <span class="font-weight-bolder h6 mb-0 poppins py-1"
                  >AI address correction</span
                >
                <span class="font-weight-normal text-muted font-size-sm pb-1">
                  Automatically detect and correct invalid or incomplete
                  shipping addresses using AI before order processing.
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-0">
                <v-checkbox
                  v-model="formData.ai_address_correction.enabled"
                  color="purple darken-2"
                  :true-value="1"
                  :false-value="0"
                >
                  <template v-slot:label>
                    <span class="h6 mb-0 text-dark-75 poppins">Enabled</span>
                  </template>
                </v-checkbox>
              </div>
              <div class="">
                <v-textarea
                  v-model="formData.ai_address_correction.description"
                  auto-grow
                  label="Description"
                  rows="4"
                  row-height="30"
                  outlined
                ></v-textarea>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Shipment Types Panel -->
          <v-expansion-panel
            class="mt-0 bg-secondary-o-50 mb-3 rounded-lg"
            v-if="!isClient"
          >
            <v-expansion-panel-header>
              <div class="d-flex flex-column align-items-start">
                <span class="font-weight-bolder h6 mb-0 poppins py-1"
                  >Shipment types</span
                >
                <span class="font-weight-normal text-muted font-size-sm pb-1">
                  Define which countries are eligible for DDP (Delivered Duty
                  Paid) or DDU (Delivered Duty Unpaid) shipment types.
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-0">
                <v-tabs
                  background-color="transparent"
                  v-model="formData.shipment_types.activeTab"
                >
                  <v-tab>DDP</v-tab>
                  <v-tab>DDU</v-tab>
                </v-tabs>

                <div class="pt-4 custom-rule-select">
                  <!-- DDP Tab -->
                  <div v-show="formData.shipment_types.activeTab === 0">
                    <div
                      v-if="formData.shipment_types.ddp.status"
                      class="w-100 mt-3 mb-8 pe-4 d-flex justify-space-between align-items-center"
                    >
                      <div class="w-50 px-4 d-flex align-items-center">
                        <span
                          class="font-weight-medium h6 mb-0 poppins py-1 me-3"
                          >Current status:</span
                        >
                        <span
                          v-if="shipment_type_statuses?.length"
                          class="text-center badge font-size-lg py-2 px-3"
                          :class="
                            getStatusBadgeClass(
                              formData.shipment_types.ddp.status
                            )
                          "
                        >
                          {{
                            shipment_type_statuses[
                              formData.shipment_types.ddp.status
                            ]?.text
                          }}
                        </span>
                      </div>
                      <div>
                        <button
                          v-if="
                            formData.shipment_types.ddp.status ===
                            RULE_STATUS.PENDING
                          "
                          class="btn btn-secondary font-weight-bolder h5 mb-0 poppins py-2"
                          @click.prevent="
                            approveShipmentType(
                              formData.shipment_types.ddp,
                              'DDP'
                            )
                          "
                        >
                          Approve
                        </button>
                      </div>
                    </div>

                    <v-autocomplete
                      v-model="formData.shipment_types.ddp.value"
                      label="DDP countries"
                      :items="getAvailableCountries('shipment_types', 'ddp')"
                      item-text="text"
                      return-object
                      outlined
                      clearable
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template v-slot:prepend-item>
                        <v-list-item
                          ripple
                          @mousedown.prevent
                          @click="toggleSelectAll('shipment_types', 'ddp')"
                        >
                          <v-list-item-action>
                            <v-icon
                              :color="
                                formData.shipment_types.ddp.value?.length > 0
                                  ? 'purple darken-2'
                                  : ''
                              "
                            >
                              {{ getSelectAllIcon("shipment_types", "ddp") }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>Select All</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2" />
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- DDU Tab -->
                  <div v-show="formData.shipment_types.activeTab === 1">
                    <div
                      v-if="formData.shipment_types.ddu.status"
                      class="w-100 mt-3 mb-8 pe-4 d-flex justify-space-between align-items-center"
                    >
                      <div class="w-50 px-4 d-flex align-items-center">
                        <span
                          class="font-weight-medium h6 mb-0 poppins py-1 me-3"
                          >Current status:</span
                        >
                        <span
                          v-if="shipment_type_statuses?.length"
                          class="text-center badge font-size-lg py-2 px-3"
                          :class="
                            getStatusBadgeClass(
                              formData.shipment_types.ddu.status
                            )
                          "
                        >
                          {{
                            shipment_type_statuses[
                              formData.shipment_types.ddu.status
                            ]?.text
                          }}
                        </span>
                      </div>
                      <div>
                        <button
                          v-if="
                            formData.shipment_types.ddu.status ===
                            RULE_STATUS.PENDING
                          "
                          class="btn btn-secondary font-weight-bolder h5 mb-0 poppins py-2"
                          @click.prevent="
                            approveShipmentType(
                              formData.shipment_types.ddu,
                              'DDU'
                            )
                          "
                        >
                          Approve
                        </button>
                      </div>
                    </div>

                    <v-autocomplete
                      v-model="formData.shipment_types.ddu.value"
                      label="DDU countries"
                      :items="getAvailableCountries('shipment_types', 'ddu')"
                      item-text="text"
                      return-object
                      outlined
                      clearable
                      multiple
                      small-chips
                      deletable-chips
                    >
                      <template v-slot:prepend-item>
                        <v-list-item
                          ripple
                          @mousedown.prevent
                          @click="toggleSelectAll('shipment_types', 'ddu')"
                        >
                          <v-list-item-action>
                            <v-icon
                              :color="
                                formData.shipment_types.ddu.value?.length > 0
                                  ? 'purple darken-2'
                                  : ''
                              "
                            >
                              {{ getSelectAllIcon("shipment_types", "ddu") }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>Select All</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2" />
                      </template>
                    </v-autocomplete>
                  </div>
                </div>
                <div class="">
                  <v-textarea
                    v-model="formData.shipment_types.description"
                    auto-grow
                    label="Description"
                    rows="4"
                    row-height="30"
                    outlined
                  ></v-textarea>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Save Button -->
        <div class="w-100 text-right my-8">
          <button
            class="btn btn-info px-6 py-3 ls1 h6"
            @click.prevent="submitData"
          >
            Save
          </button>
        </div>
      </div>
    </div>

    <LastHistory
      ref="lastHistory"
      :loadRulesFromServer="loadRulesFromServer"
      :approveStatusId="approveStatusId"
    />
  </div>
</template>

<script>
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import ApiService from "@/core/services/api.service";
import DatatableDetail from "@/own/components/datatable/DatatableDetail";
import Swal from "sweetalert2";
import LastHistory from "@/own/components/orderManagement/rules/LastHistory.vue";

// Constants
const ACTIONS = {
  HOLD: 1340,
  CANCEL: 70,
};

const SHIPMENT_TABS = {
  DDP: 1,
  DDU: 2,
};

const RULE_STATUS = {
  INACTIVE: 0,
  ACTIVE: 1,
  PENDING: 2,
};

// const ATTRIBUTE_IDS = {
//   STATUS: 6,
//   AI_PHONE: 19,
//   SHIPMENT_TYPE: 23,
//   AI_ADDRESS: 25,
// };

export default {
  name: "OMSRulesSimple",
  components: { LastHistory, DatatableDetail },

  data: () => ({
    ACTIONS,
    SHIPMENT_TABS,
    RULE_STATUS,
    ATTRIBUTE_IDS: {
      STATUS: null, // 6 actual test values,
      AI_PHONE: null, // 19 actual test values,
      AI_ADDRESS: null, // 25 actual test values,
      SHIPMENT_TYPE: null, // 23 actual test values,
    },
    indexData: null,
    customer_id: null,

    serverData: {
      sku_options: [],
      countries: [],
      clients: [],
    },

    formData: {
      sku_restriction: {
        name: "SKU Restriction",
        action: 0,
        hold: { value: null, rule_id: null },
        cancel: { value: null, rule_id: null },
        description: null,
      },
      country_restriction: {
        name: "Country Restriction",
        action: 0,
        hold: { value: null, rule_id: null },
        cancel: { value: null, rule_id: null },
        description: null,
      },
      ai_phone_correction: {
        name: "AI phone correction",
        enabled: null,
        rule_id: null,
        create_response_value: null,
        description: null,
      },
      ai_address_correction: {
        name: "AI address correction",
        enabled: null,
        rule_id: null,
        create_response_value: null,
        description: null,
      },
      shipment_types: {
        name: "Shipment types",
        activeTab: 0,
        ddp: {
          value: null,
          status: null,
          rule_id: null,
          server_values: null,
        },
        ddu: {
          value: null,
          status: null,
          rule_id: null,
          server_values: null,
        },
        description: null,
      },
    },

    shipment_type_statuses: [],
    sku_isLoading: false,
    sku_search: "",
    typingTimer: null,
    doneTypingInterval: 1500,
  }),

  computed: {
    approveStatusId() {
      return this.shipment_type_statuses?.find(
        (item) => item.text === "Approved"
      )?.index;
    },
    isIndexDataLoaded() {
      return !!this.indexData;
    },

    isClient() {
      return this.$store.getters.currentUser.data.is_client;
    },

    selectedClientRuleData() {
      const client = this.serverData.clients?.find(
        (c) => c.index == this.customer_id
      );
      return {
        id: "client",
        operator: "in",
        value: client ? [client] : [],
      };
    },

    currentSkuAction() {
      return this.formData.sku_restriction.action === 0 ? "hold" : "cancel";
    },

    currentSkuActionLabel() {
      return (
        this.currentSkuAction.charAt(0).toUpperCase() +
        this.currentSkuAction.slice(1)
      );
    },

    currentCountryAction() {
      return this.formData.country_restriction.action === 0 ? "hold" : "cancel";
    },

    currentCountryActionLabel() {
      return (
        this.currentCountryAction.charAt(0).toUpperCase() +
        this.currentCountryAction.slice(1)
      );
    },
  },

  watch: {
    sku_search(val) {
      clearTimeout(this.typingTimer);
      if (!val || val === "") return;

      this.typingTimer = setTimeout(() => {
        this.fetchSkuOptions(val.trim());
      }, this.doneTypingInterval);
    },

    customer_id: {
      handler() {
        this.loadRulesFromServer();
      },
      deep: true,
    },
  },

  beforeMount() {
    this.$store.commit(SET_PAGE_LOADING, true);
  },

  mounted() {
    this.loadInitialData();
  },

  methods: {
    // Initialization Methods
    loadInitialData() {
      ApiService.post("/order_management/order_management_simplified_rules")
        .then(({ data }) => {
          this.indexData = data.data;
          this.customer_id = this.indexData.data.rule_data.clients[0].index;
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },

    loadRulesFromServer() {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post(
        "/order_management/order_management_simplified_rules/create",
        { customer_id: this.customer_id }
      )
        .then(({ data }) => {
          this.serverData = data;
          this.mapServerDataToFormData();
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },

    // Data Mapping Methods
    mapServerDataToFormData() {
      this.resetFormData();

      this.serverData.attributes?.forEach((attribute) => {
        if (attribute.key_name === "ai_phone_correction") {
          this.ATTRIBUTE_IDS.AI_PHONE = attribute?.index;
        } else if (attribute.key_name === "ai_address_correction") {
          this.ATTRIBUTE_IDS.AI_ADDRESS = attribute?.index;
        } else if (attribute.key_name === "status") {
          this.ATTRIBUTE_IDS.STATUS = attribute?.index;
        } else if (attribute.key_name === "shipment_payment_term") {
          this.ATTRIBUTE_IDS.SHIPMENT_TYPE = attribute?.index;
        }
      });

      if (!this.serverData?.rules?.length) return;
      const rules = this.serverData.rules;
      // Map SKU Restrictions
      this.mapRuleByName(rules, "SKU Restriction Hold", (rule) => {
        this.formData.sku_restriction.action = 0;
        this.formData.sku_restriction.hold.value = this.extractRuleValue(
          rule,
          "sku"
        );

        if (rule?.id) {
          this.formData.sku_restriction.hold.rule_id = rule.id;
        }
        this.formData.sku_restriction.description = rule.description;
      });

      this.mapRuleByName(rules, "SKU Restriction Cancel", (rule) => {
        this.formData.sku_restriction.action = 1;
        this.formData.sku_restriction.cancel.value = this.extractRuleValue(
          rule,
          "sku"
        );

        if (rule?.id) {
          this.formData.sku_restriction.cancel.rule_id = rule.id;
        }
        this.formData.sku_restriction.description = rule.description;
      });

      // Map Country Restrictions
      this.mapRuleByName(rules, "Country Restriction Hold", (rule) => {
        this.formData.country_restriction.action = 0;
        this.formData.country_restriction.hold.value = this.extractRuleValue(
          rule,
          "country"
        );

        if (rule?.id) {
          this.formData.country_restriction.hold.rule_id = rule.id;
        }
        this.formData.country_restriction.description = rule.description;
      });

      this.mapRuleByName(rules, "Country Restriction Cancel", (rule) => {
        this.formData.country_restriction.action = 1;
        this.formData.country_restriction.cancel.value = this.extractRuleValue(
          rule,
          "country"
        );

        if (rule?.id) {
          this.formData.country_restriction.cancel.rule_id = rule.id;
        }
        this.formData.country_restriction.description = rule.description;
      });

      // Map AI Corrections
      this.mapRuleByName(rules, "AI address correction", (rule) => {
        this.formData.ai_address_correction.enabled = rule.attributes[0].value;
        this.formData.ai_address_correction.create_response_value =
          rule.attributes[0].value;

        if (rule?.id) {
          this.formData.ai_address_correction.rule_id = rule.id;
        }
        this.formData.ai_address_correction.description = rule.description;
      });

      this.mapRuleByName(rules, "AI phone correction", (rule) => {
        this.formData.ai_phone_correction.enabled = rule.attributes[0].value;
        this.formData.ai_phone_correction.create_response_value =
          rule.attributes[0].value;
        if (rule?.id) {
          this.formData.ai_phone_correction.rule_id = rule.id;
        }
        this.formData.ai_phone_correction.description = rule.description;
      });

      // Map Shipment Types
      this.shipment_type_statuses = this.serverData?.rule_statuses || [];

      this.mapRuleByCondition(
        rules,
        (rule) =>
          rule.name === "Shipment types" &&
          rule.attributes[0].value === SHIPMENT_TABS.DDP,
        (rule) => {
          this.formData.shipment_types.ddp.rule_id = rule.id;
          this.formData.shipment_types.ddp.status = rule.status;
          this.formData.shipment_types.ddp.value = this.extractRuleValue(
            rule,
            "country"
          );
          this.formData.shipment_types.description = rule.description;

          this.formData.shipment_types.ddp.server_values =
            this.formData.shipment_types.ddp.value
              .map((item) => item.index)
              .join(",");
        }
      );

      this.mapRuleByCondition(
        rules,
        (rule) =>
          rule.name === "Shipment types" &&
          rule.attributes[0].value === SHIPMENT_TABS.DDU,
        (rule) => {
          this.formData.shipment_types.ddu.rule_id = rule.id;
          this.formData.shipment_types.ddu.status = rule.status;
          this.formData.shipment_types.ddu.value = this.extractRuleValue(
            rule,
            "country"
          );
          this.formData.shipment_types.description = rule.description;

          this.formData.shipment_types.ddu.server_values =
            this.formData.shipment_types.ddu.value
              .map((item) => item.index)
              .join(",");
        }
      );
    },

    mapRuleByName(rules, name, callback) {
      const rule = rules.findLast((r) => r.name === name);
      if (rule) callback(rule);
    },

    mapRuleByCondition(rules, condition, callback) {
      const rule = rules.findLast(condition);
      if (rule) callback(rule);
    },

    extractRuleValue(rule, ruleId) {
      const condition = JSON.parse(rule.condition);
      return condition.rules.find((r) => r.id === ruleId)?.value || null;
    },

    hasChanged(type) {
      const currentIds = this.formData.shipment_types[type].value
        .map((c) => c.index)
        .join(",");

      return currentIds !== this.formData.shipment_types[type].server_values;
    },

    resetFormData() {
      this.formData = {
        sku_restriction: {
          name: "SKU Restriction",
          action: 0,
          hold: { value: null, rule_id: null },
          cancel: { value: null, rule_id: null },
          description: null,
        },
        country_restriction: {
          name: "Country Restriction",
          action: 0,
          hold: { value: null, rule_id: null },
          cancel: { value: null, rule_id: null },
          description: null,
        },
        ai_phone_correction: {
          name: "AI phone correction",
          enabled: null,
          rule_id: null,
          create_response_value: null,
          description: null,
        },
        ai_address_correction: {
          name: "AI address correction",
          enabled: null,
          rule_id: null,
          create_response_value: null,
          description: null,
        },
        shipment_types: {
          name: "Shipment types",
          activeTab: 0,
          ddp: {
            value: null,
            status: null,
            rule_id: null,
          },
          ddu: {
            value: null,
            status: null,
            rule_id: null,
          },
          description: null,
        },
      };
    },

    // Payload Construction
    constructPayloadData() {
      const payload = {
        customer_id: this.customer_id,
        rules: [],
      };

      // Add SKU Restrictions
      this.addRuleIfExists(
        payload,
        "SKU Restriction Hold",
        ACTIONS.HOLD,
        "sku",
        this.formData.sku_restriction.hold.value,
        this.formData.sku_restriction.hold.rule_id,
        this.formData.sku_restriction.description
      );

      this.addRuleIfExists(
        payload,
        "SKU Restriction Cancel",
        ACTIONS.CANCEL,
        "sku",
        this.formData.sku_restriction.cancel.value,
        this.formData.sku_restriction.cancel.rule_id,
        this.formData.sku_restriction.description
      );

      // Add Country Restrictions
      this.addRuleIfExists(
        payload,
        "Country Restriction Hold",
        ACTIONS.HOLD,
        "country",
        this.formData.country_restriction.hold.value,
        this.formData.country_restriction.hold.rule_id,
        this.formData.country_restriction.description
      );

      this.addRuleIfExists(
        payload,
        "Country Restriction Cancel",
        ACTIONS.CANCEL,
        "country",
        this.formData.country_restriction.cancel.value,
        this.formData.country_restriction.cancel.rule_id,
        this.formData.country_restriction.description
      );

      // Add AI Corrections
      if (
        this.formData.ai_address_correction.enabled !==
        this.formData.ai_address_correction.create_response_value
      ) {
        const rule = {
          name: "AI address correction",
          attributes: [
            {
              id: this.ATTRIBUTE_IDS.AI_ADDRESS,
              value: !!this.formData.ai_address_correction.enabled,
            },
          ],
          condition: JSON.stringify({
            rules: [this.selectedClientRuleData],
            condition: "and",
          }),
        };

        if (this.formData.ai_address_correction.rule_id) {
          rule.id = this.formData.ai_address_correction.rule_id;
        }
        rule.description = this.formData.ai_address_correction.description;
        payload.rules.push(rule);
      }

      //
      if (
        this.formData.ai_phone_correction.enabled !==
        this.formData.ai_phone_correction.create_response_value
      ) {
        const rule = {
          name: "AI phone correction",
          attributes: [
            {
              id: this.ATTRIBUTE_IDS.AI_PHONE,
              value: !!this.formData.ai_phone_correction.enabled,
            },
          ],
          condition: JSON.stringify({
            rules: [this.selectedClientRuleData],
            condition: "and",
          }),
        };

        if (this.formData.ai_phone_correction.rule_id) {
          rule.id = this.formData.ai_phone_correction.rule_id;
        }
        rule.description = this.formData.ai_phone_correction.description;

        payload.rules.push(rule);
      }

      // Add Shipment Types
      this.addShipmentTypeRule(payload, "ddp", SHIPMENT_TABS.DDP);
      this.addShipmentTypeRule(payload, "ddu", SHIPMENT_TABS.DDU);

      return payload;
    },

    addRuleIfExists(
      payload,
      name,
      actionValue,
      ruleId,
      value,
      rule_id = null,
      description
    ) {
      if (!value) return;

      const rule = {
        name,
        attributes: [{ id: this.ATTRIBUTE_IDS.STATUS, value: actionValue }],
        condition: JSON.stringify({
          rules: [
            this.selectedClientRuleData,
            { value, id: ruleId, operator: "in" },
          ],
          condition: "and",
        }),
      };

      if (rule_id) {
        rule.id = rule_id;
      }
      rule.description = description || null;
      payload.rules.push(rule);
    },

    addShipmentTypeRule(payload, type, tabValue) {
      const shipmentData = this.formData.shipment_types[type];
      if (!shipmentData.value) return;

      if (!this.hasChanged(type)) return;

      const rule = {
        name: "Shipment types",
        attributes: [{ id: this.ATTRIBUTE_IDS.SHIPMENT_TYPE, value: tabValue }],
        condition: JSON.stringify({
          rules: [
            this.selectedClientRuleData,
            { value: shipmentData.value, id: "country", operator: "in" },
          ],
          condition: "and",
        }),
      };

      if (shipmentData.rule_id) {
        rule.id = shipmentData.rule_id;
      }
      rule.description = this.formData.shipment_types.description;
      payload.rules.push(rule);
    },

    // Data Submission
    async submitData() {
      this.$store.commit(SET_PAGE_LOADING, true);

      try {
        const payload = this.constructPayloadData();

        const { rulesWithoutID, rulesWithID } = this.separateRulesByStatus(
          payload.rules
        );

        const promises = [];

        // Send new rules to /store endpoint (without IDs)
        if (rulesWithoutID.length > 0) {
          const storePayload = {
            customer_id: payload.customer_id,
            rules: rulesWithoutID,
          };
          promises.push(
            ApiService.post(
              "/order_management/order_management_simplified_rules/store",
              storePayload
            )
          );
        }

        // Send existing rules to /update endpoint (with IDs)
        if (rulesWithID.length > 0) {
          const updatePayload = {
            customer_id: payload.customer_id,
            rules: rulesWithID,
          };
          promises.push(
            ApiService.post(
              "/order_management/order_management_simplified_rules/update",
              updatePayload
            )
          );
        }

        // Execute all API calls
        await Promise.all(promises).finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });

        this.loadRulesFromServer();
        // await this.showSuccessAlert("Updated", "Data is updated!");
        await Swal.fire("Updated", "Data is successfully updated!", "success");
      } catch (error) {
        // console.error("Failed to save data:", error);
        this.$store.commit(SET_PAGE_LOADING, false);
        await Swal.fire("Warning", "Failed to save data", "warning");
      } finally {
        this.$store.commit(SET_PAGE_LOADING, false);
      }
    },

    separateRulesByStatus(rules) {
      const rulesWithoutID = [];
      const rulesWithID = [];

      rules.forEach((rule) => {
        if (rule?.id) {
          // Rule has ID - send to update endpoint
          rulesWithID.push(rule);
        } else {
          // Rule has no ID - send to store endpoint
          // Make sure to exclude any id property
          // const { id, ...ruleWithoutId } = rule;
          // rulesWithoutID.push(ruleWithoutId);
          rulesWithoutID.push(rule);
        }
      });

      return { rulesWithoutID, rulesWithID };
    },

    // SKU Search
    async fetchSkuOptions(query) {
      const requestBody = { q: query };
      if (!this.isClient) {
        requestBody.clients = [this.customer_id];
      }

      this.sku_isLoading = true;
      ApiService.post("/skus/search", requestBody)
        .then(({ data }) => {
          this.serverData.sku_options = data.skus.map((item) => ({
            index: item.skuId,
            text: item.systemCode,
          }));
        })
        .finally(() => {
          this.sku_isLoading = false;
        });
    },

    // UI Helper Methods
    getRuleDescription(name) {
      if (!this.serverData?.rules) return "";
      const rule = this.serverData.rules.find(
        (r) => r?.name && r.name.includes(name)
      );
      return rule && rule.description ? rule.description : "";
    },

    toggleSelectAll(restriction, action) {
      this.$nextTick(() => {
        const currentValue = this.formData[restriction][action].value || [];
        // const allCountries = this.serverData.countries || [];
        const allCountries = this.getAvailableCountries(restriction, action);

        const isAllSelected = currentValue.length === allCountries.length;

        this.formData[restriction][action].value = isAllSelected
          ? []
          : [...allCountries];
      });
    },

    getSelectAllIcon(restriction, action) {
      const selectedCount =
        this.formData[restriction][action].value?.length || 0;
      const totalCount = this.serverData.countries?.length || 0;

      if (selectedCount === totalCount) return "mdi-checkbox-marked";
      if (selectedCount > 0) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },

    getAvailableCountries(restriction, action) {
      if (restriction === "country_restriction") {
        const oppositeAction = action === "hold" ? "cancel" : "hold";
        const excludeFrom =
          this.formData.country_restriction[oppositeAction].value;
        return this.filterCountries(excludeFrom);
      }

      if (restriction === "shipment_types") {
        const oppositeType = action === "ddp" ? "ddu" : "ddp";
        const excludeFrom = this.formData.shipment_types[oppositeType].value;
        return this.filterCountries(excludeFrom);
      }

      return this.serverData.countries || [];
    },

    filterCountries(excludeFrom) {
      if (!excludeFrom || excludeFrom.length === 0) {
        return this.serverData.countries || [];
      }

      const excludedIndices = excludeFrom.map((item) => item.index);
      return (this.serverData.countries || []).filter(
        (country) => !excludedIndices.includes(country.index)
      );
    },

    getStatusBadgeClass(status) {
      return {
        "badge-danger": status === RULE_STATUS.INACTIVE,
        "badge-success": status === RULE_STATUS.ACTIVE,
        "badge-warning": status === RULE_STATUS.PENDING,
      };
    },

    approveShipmentType(shipmentType, type) {
      // id: this.shipmentType.rule_id
      this.$store.commit(SET_PAGE_LOADING, true);

      ApiService.post(
        "/order_management/order_management_simplified_rules/oms_rule_history",
        { id: shipmentType.rule_id }
      )
        .then(({ data }) => {
          const history = {};
          if (
            data?.data?.body?.changes_data?.before &&
            data?.data?.body?.changes_data?.after
          ) {
            history.before = this.extractRuleValue(
              data?.data?.body?.changes_data?.before.rule,
              "country"
            );

            history.after = this.extractRuleValue(
              data?.data?.body?.changes_data?.after.rule,
              "country"
            );

            this.$refs.lastHistory.toggleModal(shipmentType, history, type);
          } else {
            history.before = null;

            history.after = shipmentType.value;
            this.$refs.lastHistory.toggleModal(shipmentType, history, type);
            // Swal.fire({
            //   title: "Warning",
            //   text: "Historical data is not available for this rule",
            //   icon: "warning",
            // });
          }

          // this.dialog = false;
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
  },
};
</script>

<!--<style lang="scss">-->

<!--</style>-->
